import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Building2, 
  Wrench,
  Package,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Printer,
  Download,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';

const CaseStudyDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  useEffect(() => {
    const loadCaseStudy = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        
        // Check auth
        const authResponse = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        
        if (!authResponse.ok) {
          navigate('/');
          return;
        }
        
        setIsAuthenticated(true);
        
        // Load case studies and find the one we need
        const response = await fetch(`${API_URL}/api/case-studies`, {
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('Failed to load case studies');
        }
        
        const studies = await response.json();
        const found = studies.find(s => s.case_id === id);
        
        if (found) {
          // Filter out invalid blob URLs - only keep valid http/https URLs
          const cleanedStudy = {
            ...found,
            beforeImages: (found.beforeImages || []).filter(img => img && img.startsWith('http')),
            afterImages: (found.afterImages || []).filter(img => img && img.startsWith('http'))
          };
          setCaseStudy(cleanedStudy);
        } else {
          navigate('/customer-portal/case-studies');
        }
      } catch (error) {
        console.error('Error loading case study:', error);
        navigate('/customer-portal/case-studies');
      } finally {
        setLoading(false);
      }
    };
    
    loadCaseStudy();
  }, [navigate, id]);

  const handleDownloadPDF = () => {
    setShowPrintPreview(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const closePrintPreview = () => {
    setShowPrintPreview(false);
  };

  if (!isAuthenticated || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case study...</p>
        </div>
      </div>
    );
  }
  
  if (!caseStudy) return null;

  // Print Preview / PDF Report Component
  const PrintableReport = () => (
    <div className="print-report bg-white">
      {/* Report Header */}
      <div className="print-section border-b-4 border-blue-900 pb-6 mb-8">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <img 
              src="/bcs-logo.png" 
              alt="BCS Logo" 
              className="h-20 w-20 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-blue-900">BCS</h1>
              <p className="text-sm text-gray-600">Industrial Coating & Repair Solutions</p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-600">
            <p className="font-semibold text-blue-900 text-lg">Case Study Report</p>
            <p>Document ID: {caseStudy.case_id}</p>
            <p>Generated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Report Title Section */}
      <div className="print-section bg-blue-900 text-white p-6 rounded-lg mb-8">
        <h2 className="text-2xl font-bold mb-2">{caseStudy.title}</h2>
        <div className="flex flex-wrap gap-6 text-blue-100 text-sm">
          {caseStudy.industry && (
            <span className="flex items-center gap-1">
              <Building2 size={16} />
              {caseStudy.industry}
            </span>
          )}
          {caseStudy.location && (
            <span className="flex items-center gap-1">
              <MapPin size={16} />
              {caseStudy.location}
            </span>
          )}
          {caseStudy.dateCompleted && (
            <span className="flex items-center gap-1">
              <Calendar size={16} />
              Completed: {new Date(caseStudy.dateCompleted).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Project Overview Grid */}
      <div className="print-section grid grid-cols-3 gap-4 mb-8">
        <div className="border border-gray-200 rounded-lg p-4 text-center">
          <Wrench className="mx-auto text-blue-900 mb-2" size={28} />
          <p className="text-xs text-gray-500 uppercase tracking-wide">Application Type</p>
          <p className="font-semibold text-gray-900 text-sm mt-1">{caseStudy.applicationType}</p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 text-center">
          <Building2 className="mx-auto text-blue-900 mb-2" size={28} />
          <p className="text-xs text-gray-500 uppercase tracking-wide">Industry</p>
          <p className="font-semibold text-gray-900 text-sm mt-1">{caseStudy.industry}</p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 text-center">
          <Package className="mx-auto text-blue-900 mb-2" size={28} />
          <p className="text-xs text-gray-500 uppercase tracking-wide">Products Used</p>
          <p className="font-semibold text-gray-900 text-sm mt-1">{caseStudy.products.length} Products</p>
        </div>
      </div>

      {/* Customer Information */}
      {caseStudy.customerName && (
        <div className="print-section mb-8">
          <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4">
            Customer Information
          </h3>
          <p className="text-gray-700">{caseStudy.customerName}</p>
          {caseStudy.location && <p className="text-gray-600 text-sm">{caseStudy.location}</p>}
        </div>
      )}

      {/* Problem Description */}
      <div className="print-section mb-8">
        <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4 flex items-center gap-2">
          <AlertCircle className="text-red-600" size={20} />
          Problem Description
        </h3>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{caseStudy.problemDescription}</p>
        </div>
      </div>

      {/* Solution Applied */}
      <div className="print-section mb-8">
        <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4 flex items-center gap-2">
          <Lightbulb className="text-yellow-600" size={20} />
          Solution Applied
        </h3>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{caseStudy.solutionApplied}</p>
        </div>
      </div>

      {/* Products & Materials */}
      {caseStudy.products.length > 0 && (
        <div className="print-section mb-8">
          <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4 flex items-center gap-2">
            <Package size={20} />
            Products & Materials Used
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <ul className="grid grid-cols-2 gap-2">
              {caseStudy.products.map((product, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-blue-900 rounded-full flex-shrink-0"></span>
                  {product}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Results & Outcome */}
      {caseStudy.results && (
        <div className="print-section mb-8">
          <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4 flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            Results & Outcome
          </h3>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{caseStudy.results}</p>
          </div>
        </div>
      )}

      {/* Before/After Images */}
      {(caseStudy.beforeImages?.length > 0 || caseStudy.afterImages?.length > 0) && (
        <div className="print-section print-break-before mb-8">
          <h3 className="text-lg font-bold text-blue-900 border-b-2 border-blue-900 pb-2 mb-4">
            Project Documentation
          </h3>
          <div className="grid grid-cols-2 gap-6">
            {caseStudy.beforeImages?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-center bg-red-100 py-2 rounded">Before</h4>
                <div className="grid grid-cols-2 gap-2">
                  {caseStudy.beforeImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Before ${idx + 1}`}
                      className="w-full h-32 object-contain rounded border bg-gray-100"
                    />
                  ))}
                </div>
              </div>
            )}
            {caseStudy.afterImages?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-center bg-green-100 py-2 rounded">After</h4>
                <div className="grid grid-cols-2 gap-2">
                  {caseStudy.afterImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`After ${idx + 1}`}
                      className="w-full h-32 object-contain rounded border bg-gray-100"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Footer */}
      <div className="print-footer border-t-2 border-gray-200 pt-6 mt-8">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h4 className="font-bold text-blue-900 mb-2">About BCS</h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              BCS provides world-class industrial coating and repair solutions with 25+ years of field experience. 
              We specialize in epoxy repair composites and protective coating systems for industrial equipment, 
              structures, and critical infrastructure.
            </p>
          </div>
          <div className="text-right">
            <h4 className="font-bold text-blue-900 mb-2">Contact Us</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-semibold text-blue-900">Jason Kraft</p>
              <a href="tel:+16579668698" className="flex items-center justify-end gap-2 hover:text-blue-900">
                <Phone size={14} />
                (657) 966-8698
              </a>
              <a href="mailto:info@bcs-us.com" className="flex items-center justify-end gap-2 hover:text-blue-900">
                <Mail size={14} />
                info@bcs-us.com
              </a>
              <a href="https://www.bcs-us.com" className="flex items-center justify-end gap-2 hover:text-blue-900">
                <Globe size={14} />
                www.bcs-us.com
              </a>
            </div>
          </div>
        </div>
        <div className="text-center mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} BCS Industrial Coating & Repair Solutions. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            This document is confidential and intended for the recipient only.
          </p>
        </div>
      </div>
    </div>
  );

  // Handle print - opens a new window with just the report
  const openPrintWindow = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the report');
      return;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>BCS Case Study Report - ${caseStudy.title}</title>
        <style>
          @page {
            size: A4;
            margin: 0.5in;
          }
          
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.5;
            color: #1e293b;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .report-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 4px solid #1e3a8a;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header-left {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          
          .logo {
            width: 70px;
            height: 70px;
            object-fit: contain;
          }
          
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #1e3a8a;
          }
          
          .company-tagline {
            font-size: 12px;
            color: #6b7280;
          }
          
          .header-right {
            text-align: right;
            font-size: 12px;
            color: #6b7280;
          }
          
          .report-title {
            font-size: 16px;
            font-weight: 600;
            color: #1e3a8a;
          }
          
          .title-section {
            background: #1e3a8a;
            color: white;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .title-section h1 {
            font-size: 22px;
            margin-bottom: 10px;
          }
          
          .title-meta {
            display: flex;
            gap: 25px;
            font-size: 13px;
            color: #dbeafe;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-bottom: 18px;
            page-break-inside: avoid;
          }
          
          .info-box {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px;
            text-align: center;
          }
          
          .info-label {
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #6b7280;
            margin-bottom: 4px;
          }
          
          .info-value {
            font-size: 12px;
            font-weight: 600;
            color: #1e293b;
          }
          
          .section {
            margin-bottom: 18px;
            page-break-inside: avoid;
          }
          
          .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #1e3a8a;
            border-bottom: 2px solid #1e3a8a;
            padding-bottom: 6px;
            margin-bottom: 10px;
          }
          
          .section-content {
            padding: 12px;
            border-radius: 0 8px 8px 0;
            font-size: 14px;
            line-height: 1.7;
          }
          
          .problem-content {
            background: #fef2f2;
            border-left: 4px solid #ef4444;
          }
          
          .solution-content {
            background: #fefce8;
            border-left: 4px solid #eab308;
          }
          
          .results-content {
            background: #f0fdf4;
            border-left: 4px solid #22c55e;
          }
          
          .products-list {
            background: #f9fafb;
            padding: 15px;
            border-radius: 8px;
          }
          
          .products-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          
          .product-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
          }
          
          .product-bullet {
            width: 8px;
            height: 8px;
            background: #1e3a8a;
            border-radius: 50%;
            flex-shrink: 0;
          }
          
          .footer {
            border-top: 2px solid #e5e7eb;
            padding-top: 15px;
            margin-top: 20px;
            page-break-inside: avoid;
          }
          
          .footer-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 15px;
          }
          
          .footer-section h4 {
            font-size: 12px;
            font-weight: bold;
            color: #1e3a8a;
            margin-bottom: 6px;
          }
          
          .footer-section p {
            font-size: 11px;
            color: #6b7280;
            line-height: 1.5;
          }
          
          .footer-right {
            text-align: right;
          }
          
          .contact-item {
            display: block;
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 6px;
          }
          
          .footer-bottom {
            text-align: center;
            padding-top: 15px;
            border-top: 1px solid #e5e7eb;
          }
          
          .footer-bottom p {
            font-size: 10px;
            color: #9ca3af;
          }
          
          .images-section img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          @media print {
            .report-container {
              padding: 0;
            }
            
            img {
              max-width: 100%;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <!-- Header -->
          <div class="header">
            <div class="header-left">
              <img src="/bcs-logo.png" alt="BCS Logo" class="logo" />
              <div>
                <div class="company-name">BCS</div>
                <div class="company-tagline">Industrial Coating & Repair Solutions</div>
              </div>
            </div>
            <div class="header-right">
              <div class="report-title">Case Study Report</div>
              <div>Document ID: ${caseStudy.case_id || 'N/A'}</div>
              <div>Generated: ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
          
          <!-- Title Section -->
          <div class="title-section">
            <h1>${caseStudy.title}</h1>
            <div class="title-meta">
              <span>${caseStudy.industry || ''}</span>
              <span>${caseStudy.location || ''}</span>
              ${caseStudy.dateCompleted ? `<span>Completed: ${new Date(caseStudy.dateCompleted).toLocaleDateString()}</span>` : ''}
            </div>
          </div>
          
          <!-- Info Grid -->
          <div class="info-grid">
            <div class="info-box">
              <div class="info-label">Application Type</div>
              <div class="info-value">${caseStudy.applicationType}</div>
            </div>
            <div class="info-box">
              <div class="info-label">Industry</div>
              <div class="info-value">${caseStudy.industry}</div>
            </div>
            <div class="info-box">
              <div class="info-label">Products Used</div>
              <div class="info-value">${caseStudy.products.length} Products</div>
            </div>
          </div>
          
          ${caseStudy.customerName ? `
          <!-- Customer Information -->
          <div class="section">
            <div class="section-title">Customer Information</div>
            <p style="font-size: 14px;">${caseStudy.customerName}</p>
            ${caseStudy.location ? `<p style="font-size: 13px; color: #6b7280;">${caseStudy.location}</p>` : ''}
          </div>
          ` : ''}
          
          <!-- Problem Description -->
          <div class="section">
            <div class="section-title">Problem Description</div>
            <div class="section-content problem-content">
              ${caseStudy.problemDescription}
            </div>
          </div>
          
          <!-- Solution Applied -->
          <div class="section">
            <div class="section-title">Solution Applied</div>
            <div class="section-content solution-content">
              ${caseStudy.solutionApplied}
            </div>
          </div>
          
          ${caseStudy.products.length > 0 ? `
          <!-- Products & Materials -->
          <div class="section">
            <div class="section-title">Products & Materials Used</div>
            <div class="products-list">
              <div class="products-grid">
                ${caseStudy.products.map(p => `
                  <div class="product-item">
                    <div class="product-bullet"></div>
                    <span>${p}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>
          ` : ''}
          
          ${caseStudy.results ? `
          <!-- Results -->
          <div class="section">
            <div class="section-title">Results & Outcome</div>
            <div class="section-content results-content">
              ${caseStudy.results}
            </div>
          </div>
          ` : ''}
          
          ${(() => {
            // Filter out invalid blob URLs - only include http/https URLs
            const validBeforeImages = (caseStudy.beforeImages || []).filter(img => img && img.startsWith('http'));
            const validAfterImages = (caseStudy.afterImages || []).filter(img => img && img.startsWith('http'));
            
            if (validBeforeImages.length === 0 && validAfterImages.length === 0) {
              return '';
            }
            
            return `
          <!-- Project Documentation Images -->
          <div class="section">
            <div class="section-title">Project Documentation</div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 10px;">
              ${validBeforeImages.length > 0 ? `
              <div>
                <h4 style="font-weight: 600; color: #374151; margin-bottom: 8px; text-align: center; background: #fee2e2; padding: 6px; border-radius: 4px; font-size: 12px;">Before</h4>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;">
                  ${validBeforeImages.map((img, idx) => `
                    <img src="${img}" alt="Before ${idx + 1}" style="width: 100%; height: 100px; object-fit: contain; background: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb;" onerror="this.style.display='none'" />
                  `).join('')}
                </div>
              </div>
              ` : ''}
              ${validAfterImages.length > 0 ? `
              <div>
                <h4 style="font-weight: 600; color: #374151; margin-bottom: 8px; text-align: center; background: #dcfce7; padding: 6px; border-radius: 4px; font-size: 12px;">After</h4>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;">
                  ${validAfterImages.map((img, idx) => `
                    <img src="${img}" alt="After ${idx + 1}" style="width: 100%; height: 100px; object-fit: contain; background: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb;" onerror="this.style.display='none'" />
                  `).join('')}
                </div>
              </div>
              ` : ''}
            </div>
          </div>
          `;
          })()}
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-grid">
              <div class="footer-section">
                <h4>About BCS</h4>
                <p>BCS provides world-class industrial coating and repair solutions with 25+ years of field experience. We specialize in epoxy repair composites and protective coating systems for industrial equipment, structures, and critical infrastructure.</p>
              </div>
              <div class="footer-section footer-right">
                <h4>Contact Us</h4>
                <div class="contact-item" style="font-weight: 600; color: #1e3a8a;">Jason Kraft</div>
                <a href="tel:+16579668698" class="contact-item" style="text-decoration: none; color: #6b7280;">(657) 966-8698</a>
                <a href="mailto:info@bcs-us.com" class="contact-item" style="text-decoration: none; color: #6b7280;">info@bcs-us.com</a>
                <a href="https://www.bcs-us.com" class="contact-item" style="text-decoration: none; color: #6b7280;">www.bcs-us.com</a>
              </div>
            </div>
            <div class="footer-bottom">
              <p>© ${new Date().getFullYear()} BCS Industrial Coating & Repair Solutions. All rights reserved.</p>
              <p>This document is confidential and intended for the recipient only.</p>
            </div>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Show print preview modal
  if (showPrintPreview) {
    return (
      <div className="fixed inset-0 bg-gray-100 z-50 overflow-auto">
        {/* Toolbar */}
        <div className="sticky top-0 bg-white border-b shadow-sm px-6 py-3 flex justify-between items-center z-10">
          <div className="flex items-center gap-3">
            <img src="/bcs-logo.png" alt="BCS" className="h-10 w-10 object-contain" />
            <div>
              <h2 className="font-bold text-blue-900">Case Study Report Preview</h2>
              <p className="text-sm text-gray-600">{caseStudy.title}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={closePrintPreview}>
              <ArrowLeft size={16} className="mr-2" />
              Back to View
            </Button>
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={openPrintWindow}>
              <Printer size={16} className="mr-2" />
              Print / Save as PDF
            </Button>
          </div>
        </div>
        
        {/* Report Content Preview */}
        <div className="max-w-4xl mx-auto my-8 bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="p-8">
            <PrintableReport />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Page Header */}
      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50 no-print">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/customer-portal/case-studies')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Case Studies
          </Button>
          
          <div className="flex flex-col md:flex-row justify-between items-start gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge 
                  className={caseStudy.status === 'Published' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'}
                >
                  {caseStudy.status}
                </Badge>
                {caseStudy.dateCompleted && (
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(caseStudy.dateCompleted).toLocaleDateString()}
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                {caseStudy.title}
              </h1>
              <div className="flex flex-wrap gap-4 text-gray-600">
                <span className="flex items-center gap-1">
                  <Building2 size={16} />
                  {caseStudy.industry}
                </span>
                {caseStudy.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={16} />
                    {caseStudy.location}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(`/customer-portal/case-studies/${caseStudy.case_id}/edit`)}
                data-testid="edit-case-study-btn"
              >
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPDF}
                className="bg-blue-900 text-white hover:bg-blue-800 border-blue-900"
              >
                <Download size={16} className="mr-1" />
                Download Report
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 no-print">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Quick Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Wrench className="text-blue-900" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Application Type</p>
                    <p className="font-semibold text-slate-900">{caseStudy.applicationType}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Building2 className="text-green-700" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Industry</p>
                    <p className="font-semibold text-slate-900">{caseStudy.industry}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Package className="text-purple-700" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Products Used</p>
                    <p className="font-semibold text-slate-900">{caseStudy.products.length} Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Info */}
          {caseStudy.customerName && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 size={20} />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{caseStudy.customerName}</p>
                {caseStudy.location && (
                  <p className="text-gray-600 text-sm mt-1">{caseStudy.location}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Problem Description */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="text-red-600" size={20} />
                Problem Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{caseStudy.problemDescription}</p>
            </CardContent>
          </Card>

          {/* Solution Applied */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="text-yellow-600" size={20} />
                Solution Applied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{caseStudy.solutionApplied}</p>
            </CardContent>
          </Card>

          {/* Products Used */}
          {caseStudy.products.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package size={20} />
                  Products & Materials Used
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {caseStudy.products.map((product, idx) => (
                    <Badge key={idx} variant="outline" className="py-1 px-3">
                      {product}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {caseStudy.results && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                  <CheckCircle size={20} />
                  Results & Outcome
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-900 whitespace-pre-wrap">{caseStudy.results}</p>
              </CardContent>
            </Card>
          )}

          {/* Before/After Images */}
          {(() => {
            const validBeforeImages = (caseStudy.beforeImages || []).filter(img => img && img.startsWith('http'));
            const validAfterImages = (caseStudy.afterImages || []).filter(img => img && img.startsWith('http'));
            const hasInvalidImages = (caseStudy.beforeImages?.length || 0) + (caseStudy.afterImages?.length || 0) > validBeforeImages.length + validAfterImages.length;
            
            if (validBeforeImages.length === 0 && validAfterImages.length === 0 && !hasInvalidImages) {
              return null;
            }
            
            return (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Before & After Images</CardTitle>
                </CardHeader>
                <CardContent>
                  {hasInvalidImages && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <p className="text-yellow-800 text-sm">
                        <strong>Note:</strong> Some images need to be re-uploaded. 
                        <button 
                          onClick={() => navigate(`/customer-portal/case-studies/${caseStudy.case_id}/edit`)}
                          className="text-blue-600 underline ml-1"
                        >
                          Edit this case study
                        </button> to upload new images.
                      </p>
                    </div>
                  )}
                  {(validBeforeImages.length > 0 || validAfterImages.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {validBeforeImages.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">Before</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {validBeforeImages.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`Before ${idx + 1}`}
                                className="w-full h-32 object-contain rounded-lg bg-gray-100"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {validAfterImages.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">After</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {validAfterImages.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`After ${idx + 1}`}
                                className="w-full h-32 object-contain rounded-lg bg-gray-100"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Metadata */}
          <Card className="bg-gray-50">
            <CardContent className="pt-6">
              <div className="flex flex-wrap justify-between text-sm text-gray-600">
                <span>Case Study ID: {caseStudy.case_id}</span>
                <span>Created: {caseStudy.created_at ? new Date(caseStudy.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/customer-portal/case-studies')}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to List
            </Button>
            <Button
              className="bg-blue-900 hover:bg-blue-800"
              onClick={() => navigate('/customer-portal/case-studies/create')}
            >
              Create Another Case Study
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CaseStudyDetail;
